import { useCallback, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
//
import {
  Box,
  Card,
  Container,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { HeaderBreadcrumbs, Iconify, Page, Scrollbar } from '@/components';
import { SkeletonLoadingTable } from '@/components/skeleton';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TableSelectedActions,
} from '@/components/table';
import { InvoicesRunInvoicesListTableRow } from '../components';
import { ProviderInvoice } from '../types';
// api
import {
  getAllProviderInvoicesForRun,
  regenerateInvoiceRun,
  updateInvoice,
} from '@/features/invoices/api';
// hookes + utils
import useSettings from '@/hooks/useSettings';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import useTable, { emptyRows, getComparator } from '@/hooks/useTable';
//
import {
  PROVIDER_INVOICES_TABLE_HEAD,
  applySortFilterInvoicesList,
} from '@/features/invoices/utils';
import { PATH_USER } from '@/features/app';
import { invoicesPath } from '../routes/paths';

export default function InvoiceRunInvoicesList() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    //
    selected,
    setSelected,
    //
    onSort,
    onSelectRow,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  const { errorMsg, successMsg } = useSnackbarMsg();
  const { themeStretch } = useSettings();
  const { runId } = useParams();
  const queryClient = useQueryClient();

  // =================
  //      QUERIES
  // =================
  const { data: providerInvoices, isLoading: invoicesRunLoading } = useQuery({
    queryKey: ['providerInvoices', runId],
    queryFn: () => getAllProviderInvoicesForRun(Number(runId)),
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching invoices`);
    },
  });

  const regenerateInvoiceRunMutation = useMutation({
    mutationFn: () => regenerateInvoiceRun(Number(runId)),
    onSuccess: () => {
      successMsg('Invoice run successfully regenerated');
      queryClient.invalidateQueries(['providerInvoices', runId]);
    },
    onError: (error) => {
      errorMsg(error, 'Failed to regenerate invoice run');
    },
  });

  const markInvoicesPaidMutation = useMutation({
    mutationFn: async (invoiceIds: number[]) => {
      const results = await Promise.allSettled(
        invoiceIds.map((invoiceId) =>
          updateInvoice(invoiceId, { status_id: 2 })
        )
      );

      const failed = results
        .map((result, index) => ({ result, id: invoiceIds[index] }))
        .filter(
          (
            item
          ): item is {
            result: PromiseRejectedResult;
            id: number;
          } => item.result.status === 'rejected'
        );

      const succeededIds = results
        .map((result, index) =>
          result.status === 'fulfilled' ? invoiceIds[index] : null
        )
        .filter((id): id is number => Boolean(id));

      if (failed.length === invoiceIds.length) {
        const firstError =
          failed[0].result.reason || new Error('Failed to mark invoices');
        throw firstError;
      }

      return {
        failedIds: failed.map((item) => item.id),
        succeededIds,
        firstError: failed[0]?.result.reason,
      };
    },
    onSuccess: ({ failedIds, succeededIds, firstError }) => {
      if (succeededIds.length) {
        successMsg(
          succeededIds.length === 1
            ? 'Invoice marked as paid'
            : `${succeededIds.length} invoices marked as paid`
        );
      }

      if (failedIds.length) {
        errorMsg(
          firstError || new Error('Failed to update some invoices'),
          `Failed to mark ${failedIds.length} invoice${
            failedIds.length === 1 ? '' : 's'
          } as paid`
        );
      }

      setSelected(failedIds.map((id) => String(id)));
    },
    onError: (error) => {
      errorMsg(error, 'Failed to update invoice status');
    },
    onSettled: () => {
      queryClient.invalidateQueries(['providerInvoices', runId]);
    },
  });

  const handleRegenerateInvoices = () => regenerateInvoiceRunMutation.mutate();

  const handleMarkInvoicesPaid = () => {
    if (!providerInvoices) return;

    const unpaidInvoiceIds = providerInvoices
      .filter((invoice) => selected.includes(String(invoice.id)))
      .filter((invoice) => invoice.status_id !== 2)
      .map((invoice) => invoice.id);

    if (!unpaidInvoiceIds.length) {
      errorMsg(
        new Error('No unpaid invoices selected'),
        'Select at least one unpaid invoice to mark as paid'
      );
      return;
    }

    markInvoicesPaidMutation.mutate(unpaidInvoiceIds);
  };

  const dataFiltered = useMemo(
    () =>
      applySortFilterInvoicesList({
        tableData: providerInvoices || [],
        comparator: getComparator(order, orderBy),
      }),
    [providerInvoices, order, orderBy]
  );

  const allSelectableIds = useMemo(
    () => dataFiltered.map((row) => String(row.id)),
    [dataFiltered]
  );

  const tablePageData: ProviderInvoice[] = useMemo(
    () =>
      dataFiltered.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [dataFiltered, page, rowsPerPage]
  );

  const pageSelectableIds = useMemo(
    () => tablePageData.map((row) => String(row.id)),
    [tablePageData]
  );
  const pageSelectableIdsSet = useMemo(
    () => new Set(pageSelectableIds),
    [pageSelectableIds]
  );

  const filteredSelectedIds = useMemo(
    () => selected.filter((id) => allSelectableIds.includes(id)),
    [selected, allSelectableIds]
  );

  const pageSelectedIds = useMemo(
    () => selected.filter((id) => pageSelectableIdsSet.has(id)),
    [selected, pageSelectableIdsSet]
  );

  useEffect(() => {
    // keep selection in sync with available invoices (e.g., after refresh/filter)
    setSelected((prevSelected) =>
      {
        const nextSelected = prevSelected.filter((id) =>
          allSelectableIds.includes(id)
        );

        // avoid unnecessary state updates when selection is unchanged
        if (
          nextSelected.length === prevSelected.length &&
          nextSelected.every((id, idx) => id === prevSelected[idx])
        ) {
          return prevSelected;
        }

        return nextSelected;
      }
    );
  }, [allSelectableIds, setSelected]);

  const totalSelectedCount = filteredSelectedIds.length;
  const pageSelectedCount = pageSelectedIds.length;

  const handleSelectAllInvoices = useCallback(
    (_checked: boolean) => {
      setSelected((prevSelected) => {
        const hasAnyOnPage = prevSelected.some((id) =>
          pageSelectableIdsSet.has(id)
        );

        if (hasAnyOnPage) {
          return prevSelected.filter((id) => !pageSelectableIdsSet.has(id));
        }

        const next = new Set(prevSelected);
        pageSelectableIds.forEach((id) => next.add(id));
        return Array.from(next);
      });
    },
    [pageSelectableIds, pageSelectableIdsSet]
  );

  const denseHeight = dense ? 52 : 72;

  const isNotFound = !dataFiltered.length;

  return (
    <Page title="Provider Invoices">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Provider Invoices"
          links={[
            { name: 'Dashboard', href: PATH_USER.root },
            { name: `Invoices Run`, href: invoicesPath('list') },
            { name: `Provider Invoices - Run #${runId}`, href: '' },
          ]}
        />
        <Card>
          {invoicesRunLoading ? (
            <SkeletonLoadingTable />
          ) : (
            <>
              <LoadingButton
                onClick={handleRegenerateInvoices}
                loading={regenerateInvoiceRunMutation.isLoading}
                variant="contained"
                color={'info'}
                startIcon={<Iconify icon="eva:refresh-outline" />}
                sx={{ m: 2 }}
              >
                Regenerate Modified Invoices
              </LoadingButton>

              <Scrollbar>
                <TableContainer
                  sx={{
                    minWidth: 800,
                    position: 'relative',
                    ...(totalSelectedCount > 0 && {
                      pt: dense ? 6 : 6.5,
                    }),
                  }}
                >
                  {totalSelectedCount > 0 && (
                    <TableSelectedActions
                      dense={dense}
                      numSelected={pageSelectedCount}
                      displayCount={totalSelectedCount}
                      rowCount={pageSelectableIds.length}
                      actions={
                        <LoadingButton
                          color="success"
                          variant="contained"
                          size="small"
                          loading={markInvoicesPaidMutation.isLoading}
                          onClick={handleMarkInvoicesPaid}
                        >
                          Mark as Paid
                        </LoadingButton>
                      }
                    />
                  )}
                  <Table size={dense ? 'small' : 'medium'}>
                    <TableHeadCustom
                      order={order}
                      orderBy={orderBy}
                      headLabel={PROVIDER_INVOICES_TABLE_HEAD}
                      rowCount={pageSelectableIds.length}
                      numSelected={pageSelectedCount}
                      onSort={onSort}
                      onSelectAllRows={handleSelectAllInvoices}
                    />
                    <TableBody>
                      {tablePageData.map((preview: ProviderInvoice) => (
                        <InvoicesRunInvoicesListTableRow
                          key={preview.id}
                          row={preview}
                          selected={selected.includes(String(preview.id))}
                          onSelectRow={() => onSelectRow(String(preview.id))}
                        />
                      ))}

                      <TableEmptyRows
                        height={denseHeight}
                        emptyRows={emptyRows(
                          page,
                          rowsPerPage,
                          dataFiltered.length
                        )}
                      />

                      <TableNoData
                        isNotFound={isNotFound}
                        title="No invoices found"
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>
              <Box sx={{ position: 'relative' }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={dataFiltered.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={onChangePage}
                  onRowsPerPageChange={onChangeRowsPerPage}
                />

                <FormControlLabel
                  control={<Switch checked={dense} onChange={onChangeDense} />}
                  label="Dense"
                  sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
                />
              </Box>
            </>
          )}
        </Card>
      </Container>
    </Page>
  );
}
